// @flow

import React from 'react';
import { connect } from 'react-redux';
import withUniqueId from '../withUniqueId';
import withErrorBoundary from '../withErrorBoundary';
import {
  GalleryContainer,
  GalleryNavigation,
  GalleryImageContainer,
  GalleryDescription,
  Video,
} from './atoms';
import type { Image, Slide } from './types';
import PdbImage from '../pdbImage';
import { setUiSetting, getUiSetting } from '../../store/uiStore';

// @TODO Fix the flow typing of this component! https://justlease.atlassian.net/browse/LISTO-211

type OwnProps = {|
  images: Array<Image>,
  disclaimerText?: string,
  boxed?: boolean,
  showDisclaimer?: boolean,
  id: number,
  videoSrc?: ?string,
  videoPosition?: ?string,
  videoTitle?: ?string,
|};

type DispatchProps = {|
  handleSelectImage: Function,
|};

type StateProps = {|
  selectedSlideId: string | false,
|};

type Props = {|
  ...OwnProps,
  ...DispatchProps,
  ...StateProps,
|};

const getSlideById = (slides: Array<Slide>, id: $PropertyType<Slide, 'id'>): ?Slide => slides.find(slide => slide.id === id);

const Gallery = ({
  images,
  disclaimerText,
  boxed = false,
  showDisclaimer,
  selectedSlideId,
  handleSelectImage,
  id,
  videoSrc,
  videoPosition,
  videoTitle,
}: Props) => {
  if (images.length === 0) { return null }

  const slides = images.map((image: Image) => ({
    id: image.url,
    props: image,
    type: 'image',
  }));

  if (videoSrc) {
    const videoIndex = videoPosition === 'last' ? slides.length : 1;
    slides.splice(videoIndex, 0, {
      id: 'video',
      type: 'video',
      props: {
        title: videoTitle || 'Video',
        src: videoSrc,
      },
    });
  }

  const selectedId = selectedSlideId || images[0].url;

  const selectedSlide = getSlideById(slides, selectedId) || slides[0];

  return (
    <GalleryContainer boxed={boxed || false}>
      <GalleryImageContainer typeIsVideo={selectedSlide.type === 'video'}>
        {selectedSlide.type === 'image'
          && <PdbImage
            alt={selectedSlide.props.name}
            url={selectedSlide.props.url}
          />}
        {selectedSlide.type === 'video'
          && <Video videoSrc={selectedSlide.props.src} title={selectedSlide.props.title} />}
      </GalleryImageContainer>
      {slides.length > 1 && (
        <GalleryNavigation
          galleryId={id}
          slides={slides}
          selectedSlideId={selectedId}
          onSelectImage={slideId => handleSelectImage(slideId, id)}
        />
      )}
      {showDisclaimer && disclaimerText && (
        <GalleryDescription dangerouslySetInnerHTML={{ __html: disclaimerText }} boxed={boxed} />
      )}
    </GalleryContainer>
  );
};

Gallery.defaultProps = {
  boxed: false,
  showDisclaimer: false,
  disclaimerText: '',
  videoSrc: null,
  videoPosition: 'second',
  videoTitle: '',
};

const mapStateToProps = (state: any, { id }: OwnProps): StateProps => ({
  selectedSlideId: getUiSetting(state, `selectedSlide-${id}`),
});

const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
  handleSelectImage: (slideId: $PropertyType<Slide, 'id'>, id: string) => dispatch(setUiSetting(`selectedSlide-${id}`, slideId)),
});

export default withErrorBoundary<any, any, OwnProps, any, any>(
  withUniqueId(
    connect<Props, OwnProps, StateProps, DispatchProps, any, any>(
      mapStateToProps,
      mapDispatchToProps,
    )(Gallery),
  ),
);
